// Third-party
import React, { Component } from 'react';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';

// Components
import FormInput from './FormInput';
import { PrimaryButton, CancelButton, CloseButton } from '../buttons';
import Modal from '@/views/components/modals/AccessibleModal';
import { SelectInput, SelectOption } from '../inputs';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import notification from '../../../utils/notification';
import EnterpriseMember from '@/types/enterprise-member';
import { LicenseType, LICENSE_TYPES } from '@/types/license-type';

interface ParentProps {
  onAddMember: (fname: string, lname: string, email: string, uid?: string, nameId?: string, licenseType?: LicenseType) => Promise<EnterpriseMember>;
  onCancel: () => void;
  titleTextKey: string;
  addButtonTextKey: string;
  infoTextKey: string;
  successTextKey: string;
  useMemberUID: boolean;
  useMemberNameID: boolean;
  useLicenseType?: boolean;
}

interface FormProps {
  fname: string;
  lname: string;
  email: string;
  uid: string;
  nameId: string;
  licenseType: LicenseType;
}

type Props = ParentProps & FormikProps<FormProps>;

class AddEnterpriseMemberForm extends Component<Props> {
  static defaultProps = {
    onCancel: () => {},
    useMemberUID: false,
    useMemberNameID: false,
    useLicenseType: false,
  };

  canSubmit = () => {
    const { values, initialValues, isSubmitting, errors } = this.props;
    const isDirty = values !== initialValues;
    const hasErrors = Object.keys(errors).length > 0;
    return !hasErrors && isDirty && !isSubmitting;
  };

  render() {
    const { useMemberUID, useMemberNameID, useLicenseType, titleTextKey, infoTextKey, addButtonTextKey, isSubmitting } = this.props;

    return (
      <>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id={titleTextKey} />
          </Modal.Title>
          {!!this.props.onCancel && (
            <span>
              <CloseButton onClick={this.props.onCancel} disabled={isSubmitting} />
            </span>
          )}
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          <div className="settings-enterprise-add-member section align-items-center">
            <Form>
              <div className="d-flex justify-content-between">
                <FormInput required name="fname" placeholder="David" label={formatMessage('Admin.Enterprise.Fields.FirstName')} />
                <FormInput required name="lname" placeholder="Doe" label={formatMessage('Admin.Enterprise.Fields.LastName')} />
              </div>

              <FormInput
                required
                className="email"
                placeholder="david@capintel.io"
                type="email"
                name="email"
                label={formatMessage('Admin.Enterprise.Fields.Email')}
              />

              {useMemberUID && <FormInput className="uid" name="uid" placeholder="id" label={formatMessage('Admin.Enterprise.Fields.MemberID')} />}

              {useMemberNameID && <FormInput className="name-id" name="nameId" placeholder="id" label={formatMessage('Admin.Enterprise.Fields.NameID')} />}

              {useLicenseType && (
                <div className="field-wide">
                  <SelectInput
                    label={formatMessage('Admin.Enterprise.LicenseType')}
                    selected={this.props.values.licenseType}
                    onValueChange={(value: LicenseType) => this.props.setFieldValue('licenseType', value)}
                  >
                    <SelectOption value={LICENSE_TYPES.REGULAR}>
                      <FormattedMessage id="Admin.Enterprise.LicenseTypeRegular" />
                    </SelectOption>
                    <SelectOption value={LICENSE_TYPES.LIMITED}>
                      <FormattedMessage id="Admin.Enterprise.LicenseTypeLimited" />
                    </SelectOption>
                  </SelectInput>
                </div>
              )}

              <p>
                <FormattedMessage id={infoTextKey} />
              </p>

              <div className="form-actions d-flex justify-content-end">
                <CancelButton onClick={this.props.onCancel} disabled={isSubmitting} />

                <PrimaryButton type="submit" disabled={!this.canSubmit()} loading={isSubmitting}>
                  <FormattedMessage id={addButtonTextKey} />
                </PrimaryButton>
              </div>
            </Form>
          </div>
        </Modal.Body>
      </>
    );
  }
}

const EnterpriseMemberSchema = Yup.object().shape({
  fname: Yup.string().min(1).max(50).required(),
  lname: Yup.string().min(1).max(50).required(),
  email: Yup.string().email().required(),
  uid: Yup.string(),
  nameId: Yup.string(),
  licenseType: Yup.string().oneOf(Object.values(LICENSE_TYPES)),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema: EnterpriseMemberSchema,
  mapPropsToValues: () => ({ fname: '', lname: '', email: '', uid: '', nameId: '', licenseType: LICENSE_TYPES.REGULAR }),
  handleSubmit: (values, { props, setSubmitting, resetForm }) => {
    props
      .onAddMember(values.fname, values.lname, values.email, values.uid, values.nameId, values.licenseType)
      .then(() => {
        props.onCancel();
        notification.success(formatMessage(props.successTextKey));
        resetForm();
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(AddEnterpriseMemberForm) as any;
